import React, { useEffect, useState } from "react";
import "./Organizations.css";

export default function OrganizationsPage() {
  const [pendingOrgs, setPendingOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({
    organization_name: "",
    display_name: "",
    org_type: "",
    billing_contact_email: "",
    status: "",
    max_vcpu: 0,
    max_memory: 0,
    max_concurrent_users: 0,
    markup_level: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: ""
    }
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [addingUserOrg, setAddingUserOrg] = useState(null);
  const [newUserForm, setNewUserForm] = useState({
    name: "",
    email: "",
    phone: "",
    roles: []
  });
  const [orgCredits, setOrgCredits] = useState(null);
  const [loadingOrgCredits, setLoadingOrgCredits] = useState(false);
  const [allOrgs, setAllOrgs] = useState([]);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [loadingAllOrgs, setLoadingAllOrgs] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const paginated = filteredOrgs.slice((page-1)*pageSize, page*pageSize);
  const [allocatingCredits, setAllocatingCredits] = useState(false);
  const [allocateAmount, setAllocateAmount] = useState("");
  
  function getToken() {
    return localStorage.getItem("idToken");
  }

  function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  async function viewUsers(orgName) {
    setSelectedOrgId(orgName);
    setLoadingUsers(true);

    const token = getToken();

    const res = await fetch(
      `/api/super-admin/organizations/by-name/${encodeURIComponent(orgName)}/users`,
      {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include"
      }
    );

    const data = await res.json();
    setOrgUsers(data.users || []);
    setLoadingUsers(false);
  }

  async function enableUser(username) {
    if (!window.confirm("Enable this user?")) return;

    const token = getToken();

    await fetch("/api/super-admin/organizations/users/enable", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({ username })
    });

    viewUsers(selectedOrgId);
  }

  async function disableUser(username) {
    if (!window.confirm("Disable this user?")) return;

    const token = getToken();

    await fetch("/api/super-admin/organizations/users/disable", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({ username })
    });

    viewUsers(selectedOrgId);
  }

  async function saveUserEdits() {
    const token = getToken();

    // 1. Update name
    await fetch("/api/super-admin/organizations/users/update-attributes", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({
        username: editingUser.username,
        name: editName
      })
    });

    // 2. Update roles (array)
    await fetch("/api/super-admin/organizations/users/update-role", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({
        username: editingUser.username,
        newRoles: editRole   // <-- array of selected groups
      })
    });

    setEditingUser(null);
    viewUsers(selectedOrgId);
  }

  async function loadPending() {
    const token = getToken();

    const res = await fetch("/api/super-admin/organizations/pending", {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include"
    });

    const data = await res.json();
    setPendingOrgs(data.organizations || []);
    setLoading(false);
  }

  async function loadAllOrgs() {
    const token = getToken();

    try {
      const res = await fetch("/api/super-admin/organizations/all", {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include"
      });

      const data = await res.json();
      const orgs = data.organizations || [];

      setAllOrgs(orgs);
      setFilteredOrgs(orgs);
      setLoadingAllOrgs(false);
    } catch (err) {
      console.error("Failed to load all organizations:", err);
      setAllOrgs([]);
      setFilteredOrgs([]);
      setLoadingAllOrgs(false);
    }
  }

  useEffect(() => {
    loadAllOrgs();
    loadPending();
  }, []);
  
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrgs(allOrgs);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredOrgs(
        allOrgs.filter(o =>
          o.organization_name.toLowerCase().includes(q)
        )
      );
    }
    setPage(1); // ⭐ reset pagination
  }, [searchQuery, allOrgs]);


  async function approveOrg(id) {
    if (!window.confirm("Approve this organization?")) return;

    const token = getToken();

    await fetch("/api/super-admin/organizations/approve", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({ organization_id: id })
    });

    loadPending();
  }

  async function rejectOrg(id) {
    // Ask for reason
    const reason = window.prompt(
      "Enter a reason for rejection (optional):",
      ""
    );

    // If user pressed Cancel → do nothing
    if (reason === null) return;

    const token = getToken();

    await fetch("/api/super-admin/organizations/reject", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify({
        organization_id: id,
        reason: reason.trim() || null
      })
    });

    loadPending();
  }

  async function openEditOrg(org) {
    const token = getToken();

    const res = await fetch(
      `/api/super-admin/organizations/${org.id}/details`,
      {
        headers: {
          "Authorization": `Bearer ${token}`
        },
        credentials: "include"
      }
    );

    const data = await res.json();
    const o = data.organization;

    // ⭐ Fetch markup level
    const markupRes = await fetch(
      `/api/super-admin/organizations/${org.id}/markup`,
      {
        headers: { "Authorization": `Bearer ${token}` },
        credentials: "include"
      }
    );
    const markupData = await markupRes.json();

    setOrgForm({
      organization_name: o.organization_name,
      display_name: o.display_name || "",
      org_type: o.org_type || "",
      billing_contact_email: o.billing_contact_email || "",
      status: o.status,
      max_vcpu: o.max_vcpu,
      max_memory: o.max_memory,
      max_concurrent_users: o.max_concurrent_users,
      markup_level: markupData.markup_level ?? "",   // ⭐ ADD THIS
      address: o.address || {
        line1: "",
        line2: "",
        city: "",
        state: "",
        postal_code: "",
        country: ""
      }
    });

    setEditingOrg({ id: o.id });
    loadOrgCredits(o.id);
  }

  async function saveOrgEdits() {
    const token = getToken();

    await fetch(
      `/api/super-admin/organizations/${editingOrg.id}/update`,
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(orgForm)
      }
    );

    // ⭐ Save markup
    const normalizedMarkup =
      orgForm.markup_level === "" || orgForm.markup_level === null
        ? null
        : Number(orgForm.markup_level);

    console.log("Saving markup for org", editingOrg.id, "=>", normalizedMarkup);

    await fetch(
      `/api/super-admin/organizations/${editingOrg.id}/markup`,
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({ markup_level: normalizedMarkup })
      }
    );



    setEditingOrg(null);
    setAllocatingCredits(false);
    setAllocateAmount("");


    // Show success message
    setSuccessMessage("Organization details saved successfully.");

    // Auto-hide after 4 seconds
    setTimeout(() => setSuccessMessage(""), 4000);

    // Refresh search results or pending list
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery);
    } else {
      loadPending();
    }


    // Refresh search results
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery);
    } else {
      // If no search query, reload pending list
      loadPending();
    }
  }

  async function loadOrgCredits(orgId) {
    setLoadingOrgCredits(true);
    setOrgCredits(null);

    try {
      const token = getToken();

      const res = await fetch(
        `/api/super-admin/organizations/${orgId}/credits`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          credentials: "include"
        }
      );

      if (!res.ok) {
        console.error("Failed to load org credits, status:", res.status);
        setOrgCredits(null);
        setLoadingOrgCredits(false);
        return;
      }

      const data = await res.json();
      
      setOrgCredits(typeof data.balance === "number" ? data.balance : 0);
      setLoadingOrgCredits(false);
    } catch (err) {
      console.error("Failed to load org credits:", err);
      setLoadingOrgCredits(false);
    }
  }

  async function saveNewUser() {
    const token = getToken();

    const res = await fetch(
      `/api/super-admin/organizations/${addingUserOrg.id}/add-user`,
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(newUserForm)
      }
    );

    const data = await res.json();

    if (!res.ok) {
      alert(data.error || "Failed to add user.");
      return;
    }

    setAddingUserOrg(null);

    // Show success message
    setSuccessMessage("User added successfully.");
    setTimeout(() => setSuccessMessage(""), 4000);

    // Refresh users if viewing this org
    if (selectedOrgId === addingUserOrg.organization_name) {
      viewUsers(selectedOrgId);
    }
  }

  async function resetPassword(userSub) {
    const resp = await fetch(`/api/super-admin/reset-password/${userSub}`, {
      method: "POST"
    });

    if (resp.ok) {
      alert("Password reset email sent.");
    } else {
      alert("Failed to reset password.");
    }
  }

  async function handleBuyCredits() {
    if (!editingOrg?.id) return;
    console.log("Buy credits clicked for org", editingOrg.id);
    // Next step: call backend to create Stripe Checkout session
  }

  async function confirmAllocateCredits() {
    if (!allocateAmount || Number(allocateAmount) <= 0) {
      alert("Enter a valid whole number.");
      return;
    }

    const token = getToken();

    const res = await fetch(
      `/api/super-admin/organizations/${editingOrg.id}/allocate-credits`,
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({ amount: Number(allocateAmount) })
      }
    );

    const data = await res.json();

    if (!res.ok) {
      alert(data.error || "Failed to allocate credits.");
      return;
    }

    alert("Credits allocated successfully.");
    setAllocatingCredits(false);
    setAllocateAmount("");
    loadOrgCredits(editingOrg.id);
    loadAllOrgs();

  }

  return (
    <div className="org-page-container fade-in">
      <h1>Organizations</h1>
      {successMessage && (
        <div className="success-banner">
          {successMessage}
        </div>
      )}

      <p>Manage organizations: approvals, search, edit, and more.</p>

      <div style={{ marginBottom: "32px" }}>
        <h2 className="section-title">Search Organizations</h2>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by organization name..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}

            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              width: "300px",
              fontSize: "14px"
            }}
          />
        </div>

        <div className="org-table-wrapper" style={{ maxHeight: "500px", overflowY: "auto" }}>
          <table className="org-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Type</th>
                <th>Billing Email</th>
                <th>Created</th>
                <th>Users</th>
                <th>Add</th>
                <th>Edit</th>
              </tr>
            </thead>

            <tbody>
              {paginated.map(org => (
                <tr key={org.id}>
                  <td>{org.organization_name}</td>
                  <td>{org.status}</td>
                  <td>{org.org_type}</td>
                  <td>{org.billing_contact_email}</td>
                  <td>{new Date(org.created_on).toLocaleDateString()}</td>
                  <td>
                    <button className="btn btn-approve" onClick={() => viewUsers(org.organization_name)}>
                      View
                    </button>
                  </td>
                  <td>
                    <button className="btn btn-approve" onClick={() => { setAddingUserOrg(org); setNewUserForm({ name:"", email:"", phone:"", roles:[] }); }}>
                      Add
                    </button>
                  </td>
                  <td>
                    <button className="btn btn-secondary" onClick={() => openEditOrg(org)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, filteredOrgs.length)} of {filteredOrgs.length}
          </span>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="btn btn-secondary"
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              className="btn btn-secondary"
              disabled={page * pageSize >= filteredOrgs.length}
              onClick={() => setPage(p => (p * pageSize >= filteredOrgs.length ? p : p + 1))}
            >
              Next
            </button>
          </div>
        </div>


        {!loadingAllOrgs && filteredOrgs.length === 0 && (
          <p className="empty-state">No organizations found.</p>
        )}

      </div>

      {selectedOrgId && (
        <div style={{ marginTop: "40px" }}>
          <h2 className="section-title">Users in Organization</h2>

          {loadingUsers ? (
            <p className="loading-text">Loading users...</p>
          ) : orgUsers.length === 0 ? (
            <p className="empty-state">No users found for this organization.</p>
          ) : (
            <div className="org-table-wrapper">
              <table className="org-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Group(s)</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Action</th>
                    <th>Password</th>
                    <th>Edit</th>
                  </tr>
                </thead>

                <tbody>
                  {orgUsers.map((user) => (
                    console.log(user) || 
                    <tr key={user.id}>
                      <td>{user.name || "N/A"}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || "N/A"}</td>
                      <td>
                        {Array.isArray(user.role) && user.role.length > 0
                          ? user.role.join(", ")
                          : "N/A"}
                      </td>

                      <td>{user.status}</td>
                      <td>{new Date(user.created_on).toLocaleDateString()}</td>
                      <td>
                        {user.status === "disabled" ? (
                          <button
                            className="btn btn-approve"
                            onClick={() => enableUser(user.username)}
                          >
                            Enable
                          </button>
                        ) : (
                          <button
                            className="btn btn-reject"
                            onClick={() => disableUser(user.username)}
                          >
                            Disable
                          </button>
                        )}
                        
                      </td>
                      <td>
                        <button
                          className="btn btn-warning"
                          onClick={() => resetPassword(user.username)}
                        >
                          Reset
                        </button>

                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditingUser(user);
                            setEditName(user.name);
                            setEditRole(user.role || []);   // <-- array of groups
                          }}

                        >
                          Edit
                        </button>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editingUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit User</h3>

            <label>Name</label>
            <input
              type="text"
              className="form-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />

            <label>Roles</label>
            <select
              multiple
              className="form-input"
              value={editRole}
              onChange={(e) =>
                setEditRole(Array.from(e.target.selectedOptions, (opt) => opt.value))
              }
            >
              <option value="admin">admin</option>
              <option value="viewer">viewer</option>
              <option value="bioinformatics">bioinformatics</option>
              <option value="client">client</option>
              <option value="devops">devops</option>
            </select>


            <div className="modal-actions">
              <button className="btn btn-approve" onClick={saveUserEdits}>
                Save
              </button>
              <button className="btn btn-reject" onClick={() => {
                setEditingOrg(null);
                setAllocatingCredits(false);
                setAllocateAmount("");
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      <h2 className="section-title">Pending Approvals</h2>

      {loading ? (
        <p className="loading-text">Loading...</p>
      ) : pendingOrgs.length === 0 ? (
        <p className="empty-state">No organizations awaiting approval.</p>
      ) : (
        <div className="org-table-wrapper">
          <table className="org-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Admin Email</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {pendingOrgs.map((org) => (
                <tr key={org.id}>
                  <td>{org.organization_name}</td>
                  <td>{org.org_type}</td>
                  <td>{org.billing_contact_email}</td>
                  <td>{new Date(org.created_on).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-approve"
                      onClick={() => approveOrg(org.id)}
                    >
                      Approve
                    </button>

                    <button
                      className="btn btn-reject"
                      onClick={() => rejectOrg(org.id)}
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingOrg && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <h3>Edit Organization Details</h3>

            <div className="modal-body-grid">

              {/* LEFT COLUMN */}
              <div className="modal-section">
                <h4>Organization Details</h4>

                <label>Organization Name</label>
                <input
                  className="form-input"
                  value={orgForm.organization_name}
                  onChange={e =>
                    setOrgForm({ ...orgForm, organization_name: e.target.value })
                  }
                />
                <br/>
                <label>Display Name</label>
                <input
                  className="form-input"
                  value={orgForm.display_name}
                  onChange={e =>
                    setOrgForm({ ...orgForm, display_name: e.target.value })
                  }
                />
                
                <br/>
                <label>Org Type</label>
                  <select
                    className="form-input"
                    value={orgForm.org_type}
                    onChange={e =>
                      setOrgForm({ ...orgForm, org_type: e.target.value })
                    }
                  >
                    <option value="">Select Organization Type</option>
                    <option value="Biotech">Biotech</option>
                    <option value="Biopharma">Biopharma</option>
                    <option value="Academic Lab">Academic Lab</option>
                    <option value="Hospital">Hospital</option>
                    <option value="Nonprofit">Nonprofit</option>
                    <option value="Other">Other</option>
                  </select>

                <br/>

                <label>Status</label><br/>

                <select
                  className="form-input"
                  value={orgForm.status}
                  onChange={e =>
                    setOrgForm({ ...orgForm, status: e.target.value })
                  }
                >
                  <option value="new">new</option>
                  <option value="trial">trial</option>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>

              {/* RIGHT COLUMN */}
              <div className="modal-section">
                <h4>Limits</h4>

                <label>Max vCPU</label>
                <input
                  className="form-input"
                  type="number"
                  value={orgForm.max_vcpu}
                  onChange={e =>
                    setOrgForm({ ...orgForm, max_vcpu: Number(e.target.value) })
                  }
                />
                <br/>

                <label>Max Memory (GB)</label>
                <input
                  className="form-input"
                  type="number"
                  value={orgForm.max_memory}
                  onChange={e =>
                    setOrgForm({ ...orgForm, max_memory: Number(e.target.value) })
                  }
                />
                <br/>

                <label>Max Concurrent Users</label>
                <input
                  className="form-input"
                  type="number"
                  value={orgForm.max_concurrent_users}
                  onChange={e =>
                    setOrgForm({
                      ...orgForm,
                      max_concurrent_users: Number(e.target.value)
                    })
                  }
                />
                <br/>

                <h4>Billing</h4>

                <label>Billing Contact Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={orgForm.billing_contact_email}
                  onChange={e =>
                    setOrgForm({
                      ...orgForm,
                      billing_contact_email: e.target.value
                    })
                  }
                />
                <br/>
                <label>Markup Level (%)</label><br/>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  value={orgForm.markup_level}
                  onChange={e =>
                    setOrgForm({
                      ...orgForm,
                      markup_level: e.target.value
                    })
                  }
                />

                <br/>
                <h4>Credits</h4>

                {loadingOrgCredits ? (
                  <p>Loading credits...</p>
                ) : (
                  <p>
                    Current Credits:{" "}
                    {orgCredits === null ? "—" : orgCredits.toFixed(2)}
                  </p>
                )}

                <button
                  className="btn btn-approve"
                  type="button"
                  onClick={() => setAllocatingCredits(true)}
                  style={{ marginTop: "8px" }}
                >
                  Allocate Credits
                </button>
                {allocatingCredits && (
                <div style={{ marginTop: "12px" }}>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="form-input"
                    placeholder="Enter credits to allocate"
                    value={allocateAmount}
                    onChange={e => setAllocateAmount(e.target.value)}
                  />

                  <button
                    className="btn btn-save"
                    style={{ marginLeft: "8px" }}
                    onClick={confirmAllocateCredits}
                  >
                    Confirm
                  </button>

                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: "8px" }}
                    onClick={() => { setAllocatingCredits(false); setAllocateAmount(""); }}
                  >
                    Cancel
                  </button>
                </div>
              )}


              </div>
            </div>

            {/* ADDRESS SECTION FULL WIDTH */}
            <div className="modal-section-full">
              <h4>Address</h4>

              <div className="modal-body-grid">

                <div>
                  <label>Address Line 1</label>
                  <input
                    className="form-input"
                    value={orgForm.address.line1}
                    onChange={e =>
                      setOrgForm({
                        ...orgForm,
                        address: { ...orgForm.address, line1: e.target.value }
                      })
                    }
                  />
                </div>

                <div>
                  <label>Address Line 2</label>
                  <input
                    className="form-input"
                    value={orgForm.address.line2}
                    onChange={e =>
                      setOrgForm({
                        ...orgForm,
                        address: { ...orgForm.address, line2: e.target.value }
                      })
                    }
                  />
                </div>

                <div>
                  <label>City</label><br/>
                  <input
                    className="form-input"
                    value={orgForm.address.city}
                    onChange={e =>
                      setOrgForm({
                        ...orgForm,
                        address: { ...orgForm.address, city: e.target.value }
                      })
                    }
                  />
                </div>

                <div>
                  <label>State</label>
                  <input
                    className="form-input"
                    value={orgForm.address.state}
                    onChange={e =>
                      setOrgForm({
                        ...orgForm,
                        address: { ...orgForm.address, state: e.target.value }
                      })
                    }
                  />
                </div>

                <div>
                  <label>Postal Code</label>
                  <input
                    className="form-input"
                    value={orgForm.address.postal_code}
                    onChange={e =>
                      setOrgForm({
                        ...orgForm,
                        address: { ...orgForm.address, postal_code: e.target.value }
                      })
                    }
                  />
                </div>

                <div>
                  <label>Country</label>
                  <input
                    className="form-input"
                    value={orgForm.address.country}
                    onChange={e =>
                      setOrgForm({
                        ...orgForm,
                        address: { ...orgForm.address, country: e.target.value }
                      })
                    }
                  />
                </div>

              </div>
            </div>


            <div className="modal-actions">
              <button className="btn btn-approve" onClick={saveOrgEdits}>
                Save
              </button>
              <button className="btn btn-reject" onClick={() => setEditingOrg(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {addingUserOrg && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add User to {addingUserOrg.organization_name}</h3>

            <label>Name</label>
            <input
              className="form-input"
              value={newUserForm.name}
              onChange={(e) =>
                setNewUserForm({ ...newUserForm, name: e.target.value })
              }
            />

            <label>Email</label>
            <input
              className="form-input"
              type="email"
              value={newUserForm.email}
              onChange={(e) =>
                setNewUserForm({ ...newUserForm, email: e.target.value })
              }
            />

            <label>Phone (optional)</label>
            <input
              className="form-input"
              value={newUserForm.phone}
              onChange={(e) =>
                setNewUserForm({ ...newUserForm, phone: e.target.value })
              }
            />

            <label>Roles</label>
            <select
              multiple
              className="form-input"
              value={newUserForm.roles}
              onChange={(e) =>
                setNewUserForm({
                  ...newUserForm,
                  roles: Array.from(e.target.selectedOptions, (opt) => opt.value)
                })
              }
            >
              <option value="admin">admin</option>
              <option value="viewer">viewer</option>
              <option value="bioinformatics">bioinformatics</option>
              <option value="client">client</option>
              <option value="devops">devops</option>
            </select>

            <div className="modal-actions">
              <button className="btn btn-approve" onClick={saveNewUser}>
                Save
              </button>
              <button
                className="btn btn-reject"
                onClick={() => setAddingUserOrg(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}